import { gql } from '@apollo/client';

// ==================== Fragments ====================

export const DOCUMENT_FRAGMENT = gql`
  fragment DocumentFields on Document {
    id
    organizationId
    name
    originalFilename
    category
    s3Key
    mimeType
    sizeBytes
    description
    uploadedBy
    isArchived
    archivedAt
    createdAt
    updatedAt
  }
`;

// ==================== Queries ====================

export const GET_DOCUMENTS = gql`
  query GetDocuments(
    $organizationId: ID!
    $category: DocumentCategory
    $includeArchived: Boolean
    $limit: Int
    $offset: Int
  ) {
    documents(
      organizationId: $organizationId
      category: $category
      includeArchived: $includeArchived
      limit: $limit
      offset: $offset
    ) {
      items {
        ...DocumentFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        currentPage
      }
    }
  }
  ${DOCUMENT_FRAGMENT}
`;

export const GET_DOCUMENT = gql`
  query GetDocument($id: ID!) {
    document(id: $id) {
      ...DocumentFields
    }
  }
  ${DOCUMENT_FRAGMENT}
`;

// ==================== Mutations ====================

export const REQUEST_UPLOAD_URL = gql`
  mutation RequestUploadUrl($input: RequestUploadUrlInput!) {
    requestUploadUrl(input: $input) {
      uploadUrl
      s3Key
      expiresIn
    }
  }
`;

export const CONFIRM_UPLOAD = gql`
  mutation ConfirmUpload($input: ConfirmUploadInput!) {
    confirmUpload(input: $input) {
      ...DocumentFields
    }
  }
  ${DOCUMENT_FRAGMENT}
`;

export const REQUEST_DOWNLOAD_URL = gql`
  mutation RequestDownloadUrl($documentId: ID!) {
    requestDownloadUrl(documentId: $documentId) {
      downloadUrl
      expiresIn
    }
  }
`;

export const UPDATE_DOCUMENT = gql`
  mutation UpdateDocument($id: ID!, $input: UpdateDocumentInput!) {
    updateDocument(id: $id, input: $input) {
      ...DocumentFields
    }
  }
  ${DOCUMENT_FRAGMENT}
`;

export const DELETE_DOCUMENT = gql`
  mutation DeleteDocument($id: ID!) {
    deleteDocument(id: $id)
  }
`;

export const ARCHIVE_DOCUMENT = gql`
  mutation ArchiveDocument($id: ID!) {
    archiveDocument(id: $id)
  }
`;

export const UNARCHIVE_DOCUMENT = gql`
  mutation UnarchiveDocument($id: ID!) {
    unarchiveDocument(id: $id)
  }
`;

// ==================== TypeScript Types ====================

export type DocumentCategory = 'CONTRACT' | 'SOW' | 'NDA' | 'AMENDMENT' | 'INSURANCE' | 'OTHER';

export interface Document {
  id: string;
  organizationId: string;
  name: string;
  originalFilename: string;
  category: DocumentCategory;
  s3Key: string;
  mimeType?: string;
  sizeBytes?: number;
  description?: string;
  uploadedBy: string;
  isArchived: boolean;
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export interface DocumentConnection {
  items: Document[];
  pageInfo: PageInfo;
}

// Query variable types
export interface GetDocumentsVariables {
  organizationId: string;
  category?: DocumentCategory;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
}

export interface GetDocumentVariables {
  id: string;
}

// Mutation input types
export interface RequestUploadUrlInput {
  organizationId: string;
  filename: string;
  category: DocumentCategory;
  contentType?: string;
}

export interface ConfirmUploadInput {
  organizationId: string;
  name: string;
  originalFilename: string;
  category: DocumentCategory;
  s3Key: string;
  mimeType?: string;
  sizeBytes?: number;
  description?: string;
}

export interface UpdateDocumentInput {
  name?: string;
  category?: DocumentCategory;
  description?: string;
}

// Query/Mutation response types
export interface GetDocumentsData {
  documents: DocumentConnection;
}

export interface GetDocumentData {
  document: Document | null;
}

export interface RequestUploadUrlData {
  requestUploadUrl: {
    uploadUrl: string;
    s3Key: string;
    expiresIn: number;
  };
}

export interface ConfirmUploadData {
  confirmUpload: Document;
}

export interface RequestDownloadUrlData {
  requestDownloadUrl: {
    downloadUrl: string;
    expiresIn: number;
  };
}

export interface UpdateDocumentData {
  updateDocument: Document;
}

export interface DeleteDocumentData {
  deleteDocument: boolean;
}

export interface ArchiveDocumentData {
  archiveDocument: boolean;
}

export interface UnarchiveDocumentData {
  unarchiveDocument: boolean;
}

// ==================== Helper Functions ====================

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * Map category to display label
 */
export const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  CONTRACT: 'Executed Contract',
  SOW: 'Statement of Work',
  NDA: 'NDA',
  AMENDMENT: 'Amendment',
  INSURANCE: 'Insurance Certificate',
  OTHER: 'Other',
};

/**
 * Upload a file directly to S3 using a presigned URL
 */
export async function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });
    
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}
